import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Trash2, Calendar, Clock, Download, Loader2, Package,
  CheckCircle2, Truck, FileSpreadsheet, AlertTriangle, ShieldCheck
} from "lucide-react";

const STATUS_CONFIG = {
  pending:     { label: "Demande reçue",      icon: Clock,        bg: "bg-white/50/20",   text: "text-slate-400",   border: "border-slate-500/30" },
  received:    { label: "Demande reçue",      icon: Clock,        bg: "bg-white/50/20",   text: "text-slate-400",   border: "border-slate-500/30" },
  scheduled:   { label: "Planifiée",          icon: Calendar,     bg: "bg-blue-500/100/20",    text: "text-blue-400",    border: "border-blue-500/30" },
  collecting:  { label: "En cours de collecte", icon: Truck,      bg: "bg-amber-500/100/20",   text: "text-amber-400",   border: "border-amber-500/30" },
  destroying:  { label: "En destruction",     icon: Trash2,       bg: "bg-orange-500/20",  text: "text-orange-400",  border: "border-orange-500/30" },
  in_progress: { label: "En cours",           icon: Trash2,       bg: "bg-orange-500/20",  text: "text-orange-400",  border: "border-orange-500/30" },
  certified:   { label: "Certificat disponible", icon: ShieldCheck, bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" },
  completed:   { label: "Certificat disponible", icon: CheckCircle2, bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" },
};

const PRIORITY_STYLES = {
  normal:  "bg-white/50/20 text-slate-400 border-slate-500/30",
  urgent:  "bg-amber-500/100/20 text-amber-400 border-amber-500/30",
  express: "bg-red-500/100/20 text-red-400 border-red-500/30 animate-pulse",
};

const STEPS = [
  { key: "received",   label: "Reçue",     icon: Clock },
  { key: "scheduled",  label: "Planifiée", icon: Calendar },
  { key: "collecting", label: "Collecte",  icon: Truck },
  { key: "destroying", label: "Destruction", icon: Trash2 },
  { key: "certified",  label: "Certificat", icon: ShieldCheck },
];

const getStepIndex = (status) => {
  const map = { pending: 0, received: 0, scheduled: 1, collecting: 2, destroying: 3, in_progress: 3, certified: 4, completed: 4 };
  return map[status] ?? 0;
};

const DestructionsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [destructions, setDestructions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [downloadingCert, setDownloadingCert] = useState(null);

  useEffect(() => { fetchDestructions(); }, [statusFilter]);

  const fetchDestructions = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const res = await axios.get(`${API}/destructions`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setDestructions(res.data);
    } catch { toast.error("Erreur lors du chargement"); }
    finally { setLoading(false); }
  };

  const downloadCertificate = async (destructionId) => {
    setDownloadingCert(destructionId);
    try {
      const res = await axios.get(`${API}/certificates/${destructionId}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificat_${destructionId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Certificat téléchargé");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur téléchargement"); }
    finally { setDownloadingCert(null); }
  };

  const fmt = (v) => new Intl.NumberFormat("fr-CI", { style: "currency", currency: "XOF", maximumFractionDigits: 0 }).format(v);

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="destructions-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-white">Suivi des Destructions</h1>
            <p className="text-slate-400 mt-1">Historique et suivi de vos demandes de destruction</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter-select" className="w-full md:w-52 bg-slate-900/50 border-white/10 text-white">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10">
              <SelectItem value="all" className="text-slate-300">Tous les statuts</SelectItem>
              <SelectItem value="pending" className="text-slate-300">Demande reçue</SelectItem>
              <SelectItem value="scheduled" className="text-slate-300">Planifiée</SelectItem>
              <SelectItem value="collecting" className="text-slate-300">En cours de collecte</SelectItem>
              <SelectItem value="destroying" className="text-slate-300">En destruction</SelectItem>
              <SelectItem value="certified" className="text-slate-300">Certificat disponible</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
          </div>
        ) : destructions.length > 0 ? (
          <div className="space-y-4">
            {destructions.map((d) => {
              const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.pending;
              const Icon = cfg.icon;
              const stepIdx = getStepIndex(d.status);
              const canDownloadCert = ["certified", "completed"].includes(d.status);

              return (
                <Card key={d.id} className="glass-card overflow-hidden" data-testid={`destruction-${d.id}`}>
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between p-4 border-b border-white/5">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${cfg.bg}`}>
                          <Icon className={`w-5 h-5 ${cfg.text}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-3">
                            <p className="text-white font-mono font-medium">#{d.id.slice(0, 8).toUpperCase()}</p>
                            <Badge variant="outline" className={PRIORITY_STYLES[d.priority] || PRIORITY_STYLES.normal}>
                              {d.priority === "express" ? "EXPRESS" : d.priority === "urgent" ? "Urgent" : "Normal"}
                            </Badge>
                          </div>
                          <p className="text-slate-500 text-sm mt-1">
                            {new Date(d.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-3 md:mt-0">
                        <Badge className={`${cfg.bg} ${cfg.text} ${cfg.border} border`}>{cfg.label}</Badge>
                        {canDownloadCert && (
                          <Button variant="outline" size="sm" onClick={() => downloadCertificate(d.id)}
                            disabled={downloadingCert === d.id} data-testid={`download-cert-${d.id}`}
                            className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10 gap-2">
                            {downloadingCert === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            Certificat
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Progress Steps */}
                    <div className="px-4 pt-4 pb-2">
                      <div className="flex items-center justify-between">
                        {STEPS.map((step, i) => {
                          const StepIcon = step.icon;
                          const done = i <= stepIdx;
                          const active = i === stepIdx;
                          return (
                            <div key={step.key} className="flex flex-col items-center flex-1">
                              <div className="flex items-center w-full">
                                {i > 0 && <div className={`flex-1 h-0.5 ${done ? "bg-emerald-500" : "bg-slate-700"}`} />}
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all
                                  ${active ? "bg-sky-500 ring-2 ring-sky-500/30" : done ? "bg-emerald-500/80" : "bg-slate-700"}`}>
                                  <StepIcon className={`w-3.5 h-3.5 ${done ? "text-white" : "text-slate-500"}`} />
                                </div>
                                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < stepIdx ? "bg-emerald-500" : "bg-slate-700"}`} />}
                              </div>
                              <span className={`text-[10px] mt-1.5 ${active ? "text-sky-400 font-medium" : done ? "text-emerald-400" : "text-slate-600"}`}>
                                {step.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Détails */}
                    <div className="p-4 grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="md:col-span-2">
                        <p className="text-slate-500 text-sm mb-2">Produits</p>
                        <div className="space-y-1.5 max-h-28 overflow-y-auto">
                          {d.products?.map((p, i) => (
                            <div key={i} className="flex items-center justify-between p-1.5 rounded bg-slate-950/50">
                              <div className="flex items-center gap-2">
                                <Package className="w-3.5 h-3.5 text-slate-500" />
                                <span className="text-slate-300 text-sm">{p.name}</span>
                              </div>
                              <span className="text-slate-500 text-sm">x{p.quantity}{p.unit_weight ? ` (${(p.quantity * p.unit_weight).toFixed(2)} kg)` : ""}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm mb-2">Valeur totale</p>
                        <p className="text-2xl font-outfit font-bold text-amber-400">{fmt(d.total_value)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm mb-2">Poids</p>
                        <p className="text-lg font-outfit font-bold text-white">
                          {d.attestation_weight ? `${d.attestation_weight} kg` : d.total_weight ? `${d.total_weight.toFixed(2)} kg` : "N/A"}
                        </p>
                        {d.attestation_weight && (
                          <span className="text-[10px] text-emerald-400 font-medium">Atteste par autorites</span>
                        )}
                        {!d.attestation_weight && d.total_weight > 0 && (
                          <span className="text-[10px] text-slate-500">Estime (a confirmer)</span>
                        )}
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm mb-2">Motif</p>
                        <p className="text-slate-300 text-sm">{d.reason}</p>
                        {d.scheduled_date && (
                          <p className="text-sky-400 text-sm mt-2 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Prevu le {new Date(d.scheduled_date).toLocaleDateString("fr-FR")}
                          </p>
                        )}
                      </div>
                    </div>

                    {canDownloadCert && d.completed_at && (
                      <div className="px-4 pb-4">
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                          <ShieldCheck className="w-5 h-5 text-emerald-400" />
                          <span className="text-emerald-400 text-sm">
                            Certificat disponible depuis le {new Date(d.completed_at).toLocaleDateString("fr-FR")}
                          </span>
                          {d.certificate_id && (
                            <span className="ml-auto text-slate-500 text-xs font-mono">CERT-{d.certificate_id.slice(0, 8).toUpperCase()}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <Trash2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-outfit text-white mb-2">Aucune destruction</h3>
              <p className="text-slate-500">Aucune demande ne correspond au filtre sélectionné.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default DestructionsPage;
